import type { CreateHouseholdDto, DeclaredExpenditure, Household } from '@api/btlApplicationForm/types';
import { mapOoExpensesFormValuesToHousehold } from '../Expenses/mapOoExpensesFormValuesToHousehold';
import { mapHouseholdToOoExpensesFormValues } from '../Expenses/mapHouseholdToOoExpensesFormValues';

export interface PropertyOutgoingsFormValues {
  councilTax?: number;
  serviceChargeGroundRent?: number;
}

function toNonNegativeNumber(value: unknown): number {
  if (typeof value === 'number' && Number.isFinite(value) && value >= 0) {
    return value;
  }

  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value);

    if (Number.isFinite(parsed) && parsed >= 0) {
      return parsed;
    }
  }

  return 0;
}

/**
 * Leaves the input empty rather than showing `£0`. A prefilled 0 satisfies the required rule on
 * Council tax, so the broker could click through without ever entering a figure. Stored zeros are
 * treated the same way: the Household form used to coerce blanks to 0, so a 0 means "never asked".
 */
function toOptionalAmount(value: unknown): number | undefined {
  const amount = toNonNegativeNumber(value);

  return amount > 0 ? amount : undefined;
}

export function mapDeclaredExpenditureToPropertyOutgoingsFormValues(
  declaredExpenditure?: Pick<DeclaredExpenditure, 'councilTax' | 'serviceChargeGroundRent'> | null,
): PropertyOutgoingsFormValues {
  return {
    councilTax: toOptionalAmount(declaredExpenditure?.councilTax),
    serviceChargeGroundRent: toOptionalAmount(declaredExpenditure?.serviceChargeGroundRent),
  };
}

export function mapPropertyOutgoingsFormValuesToDeclaredExpenditurePatch(
  propertyOutgoings: PropertyOutgoingsFormValues | Record<string, unknown> | undefined,
): Pick<DeclaredExpenditure, 'councilTax' | 'serviceChargeGroundRent'> {
  const source = (propertyOutgoings ?? {}) as Record<string, unknown>;

  return {
    councilTax: toNonNegativeNumber(source.councilTax),
    serviceChargeGroundRent: toNonNegativeNumber(source.serviceChargeGroundRent),
  };
}

/**
 * Builds a full household write payload that preserves existing household data while
 * updating only the property-outgoing declared-expenditure fields.
 */
export function buildHouseholdPayloadWithPropertyOutgoings(
  household: Household | undefined,
  propertyOutgoings: PropertyOutgoingsFormValues | Record<string, unknown> | undefined,
): CreateHouseholdDto {
  const base = mapOoExpensesFormValuesToHousehold(mapHouseholdToOoExpensesFormValues(household));
  const patch = mapPropertyOutgoingsFormValuesToDeclaredExpenditurePatch(propertyOutgoings);

  return {
    ...base,
    declaredExpenditure: {
      ...base.declaredExpenditure,
      ...patch,
    },
  };
}
